import {
  type IPolicy as IPolicyResponse,
  type IPolicyPolicyData,
  type IPolicyPolicyDataBeneficiary,
  type IPolicyPolicyDataPayment,
  type IPolicyPolicyDataPhase,
  type IPolicyReinstatement,
} from '@/shared/api/models.bff';
import { mapBeneficiaryResponse, type IBeneficiary } from './Beneficiary';
import { mapReinstatementResponse, type IReinstatement } from './Reinstatement';
import { mapOwnerResponse, type IOwner } from './Owner';
import { mapPhaseResponse, type IPhase } from './Phase';
import { mapPersonResponse } from './Person';
import type { IPolicyStorePolicy, IPolicyStorePolicyPayments } from '../store/policyStore.model';
import { mapAgentResponse } from './Agent';
import { parse } from 'date-fns';

export const parseDate = (dateString: string, format: string = 'yyyy-MM-dd') => {
  if (typeof dateString !== 'string' || dateString.length === 0) {
    return null;
  }

  const date = parse(dateString, format, new Date());
  return Number.isNaN(date.valueOf()) ? null : date;
};

export const parseNum = (number: number | string) => {
  const num = +number;
  return !Number.isNaN(num) ? num : 0.0;
};

const _setPayments = (payments?: IPolicyPolicyDataPayment[]): IPolicyStorePolicyPayments[] => {
  const _payments = (payments ?? []).map((payment) => {
    const parsedDate = parseDate(payment.PaymentDate);
    return {
      date: parsedDate ? parsedDate.toISOString() : null,
      amount: payment.PaymentAmount,
    };
  });
  return _payments;
};
const _setBeneficiaries = (beneficiaries?: IPolicyPolicyDataBeneficiary[]): IBeneficiary[] => {
  const _beneficiaries = (beneficiaries ?? []).map((beneficiary: IPolicyPolicyDataBeneficiary) =>
    mapBeneficiaryResponse(beneficiary)
  );
  return _beneficiaries;
};
const _setPhases = (phases?: IPolicyPolicyDataPhase[]): IPhase[] => {
  const _phases = (phases ?? []).map((phase) => mapPhaseResponse(phase));
  return _phases;
};
const _setAnnuity = (phases?: IPolicyPolicyDataPhase[]): IPhase[] => {
  const _phases = _setPhases(phases);
  return _phases.filter((phase) => phase.isAnnuity);
};
const _setChildRider = (phases: IPolicyPolicyDataPhase[]): IPhase[] => {
  const _phases = _setPhases(phases);
  return _phases.filter(({ person }) => person === '20');
};
const _setOwner = (policyData: IPolicyPolicyData): IOwner => {
  const _owner = mapOwnerResponse(policyData);
  return _owner;
};
// const _setPaymentInfo = (paymentInfo: IPolicyPolicyData): IPaymentInfo => {
//   const _paymentInfo = mapPaymentInfoResponse(paymentInfo);
//   return _paymentInfo;
// };
const _setReinstatement = (reinstatement: IPolicyReinstatement): IReinstatement => {
  const _reinstatement = mapReinstatementResponse(reinstatement);
  return _reinstatement;
};

const _isCanadaQuebecBeneficiaryPolicy = (phases: IPhase[], owner?: IOwner) => {
  let isQuebec = false;
  phases.forEach((phase: IPhase) => {
    if (phase.person === '01' && phase.issueState === 'PQ') {
      isQuebec = true;
    }
  });
  if (owner?.address.state !== 'PQ') {
    isQuebec = false;
  }
  return isQuebec;
};
const _isCanadaPolicy = (contractId: string) => {
  const companyCode = contractId.slice(0, 2);
  return companyCode === '10' || companyCode === '05';
};
const _allowEditBeneficiary = (
  isCanadaPolicy: boolean,
  phases: IPhase[] | undefined,
  thirdPartyPolicy: boolean | undefined
) => {
  // US client validation, allow to edit
  if (!isCanadaPolicy) {
    return true;
  }
  // Canada client validation, allow to edit if
  // 1. not thirdPartyPolicy
  // 2. phase.issueState is in list of stated (configured in config file)
  if (thirdPartyPolicy) {
    return false;
  }
  const editBeneficiaryCanada = window.appConfig.life.editBeneficiaryCanada;
  if (!editBeneficiaryCanada) {
    return false;
  }
  if (!phases || phases.length === 0) {
    return false;
  }
  let allowed = false;
  phases.forEach((phase: IPhase) => {
    if (phase.person === '01' && editBeneficiaryCanada.includes(phase.issueState)) {
      allowed = true;
    }
  });
  return allowed;
};
const _dateFormat = (isCanadaQuebecPolicy: boolean, isCanadaPolicy: boolean) => {
  if (isCanadaQuebecPolicy) {
    return 'YYYY/MM/DD';
  } else if (isCanadaPolicy) {
    return 'DD/MM/YYYY';
  }
  return 'MM/DD/YYYY';
};
const _isNewYorkPolicy = (contractId: string): boolean => {
  return contractId.startsWith('34');
};
const _isUSPolicy = (contractId: string): boolean => {
  return contractId.startsWith('04');
};
const _country = (isCanadaPolicy: boolean) => {
  return isCanadaPolicy ? 'ca' : 'us';
};

/* const _mapDerivedData = (p,) => {
  const isCanadaQuebecPolicy = p.owner.address.state === 'PQ';
  const isCanadaQuebecBeneficiaryPolicy = _isCanadaQuebecBeneficiaryPolicy(p.phases, p.owner);
  const isCanadaPolicy = _isCanadaPolicy(contractId);
  const allowEditBeneficiary = _allowEditBeneficiary(isCanadaPolicy, p.phases);
  const dateFormat = _dateFormat(isCanadaQuebecPolicy, isCanadaPolicy);
  const isNewYorkPolicy = _isNewYorkPolicy(contractId);
  const isUSPolicy = _isUSPolicy(contractId);
  const country = _country(isCanadaPolicy);
  const d = {
    isCanadaQuebecPolicy,
    isCanadaQuebecBeneficiaryPolicy,
    isCanadaPolicy,
    allowEditBeneficiary,
    dateFormat,
    isNewYorkPolicy,
    isUSPolicy,
    country,
  };
  const mappedPolicy = { ...t, ...p, ...n, ...r, ...d };
} */

export const mapPolicyResponse = ({
  contractId, // *? !!
  policyStatusDesc, // *? !!
  showAddChildLink, // *? !!
  showOneTimePayment, // *? !!
  payNowLinkDesc = '', // *?
  kubraPaymentStatus, // *?
  agentId, // *? !!
  // documents, // *?
  servicingAgent, // object
  servicingRVP, // object
  policyData, // object
  totalAnnuityCoverage, // data !!
  totalChildCoverage, // data !!
  totalSpouseCoverage, // data !!
  totalPrimaryCoverage, // data !!
  totalActivePrimaryCoverage, // data !!
  totalActiveSpouseCoverage, // data !!
  access, // data !!
  allowAllOtherUpdates, // data !!
  allowContactInfoUpdate, // data !!
  allowDocumentsPrint, // data !!
  allowOneTimePayment, // data
  allowPaymentInfoUpdates, // data !!
  insuredFirstName, // !!
  insuredLastName, // !!
  reinstatement, // object
}: IPolicyResponse): IPolicyStorePolicy => {
  const _data = {
    totalActivePrimaryCoverage,
    totalActiveSpouseCoverage,
    totalAnnuityCoverage,
    totalChildCoverage,
    totalPrimaryCoverage,
    totalSpouseCoverage,
    access,
    allowAllOtherUpdates,
    allowContactInfoUpdate,
    allowDocumentsPrint,
    allowOneTimePayment,
    allowPaymentInfoUpdates,
    ...policyData,
  };

  const a: Partial<IPolicyStorePolicy> = {
    servicingAgent: servicingAgent ? mapAgentResponse(servicingAgent) : undefined,
    servicingRVP: servicingRVP ? mapAgentResponse(servicingRVP) : undefined,
  };

  const t: Partial<IPolicyStorePolicy> = {
    contractId,
    payNowLinkDesc,
    agentId,
    kubraPaymentStatus,
    // documents,
    policyStatusDesc,
    showPayNowLink: showOneTimePayment === 'true',
    showAddChildLink: showAddChildLink === 'true',

    // data
    data: _data,

    // remapping ???
    activePrimaryCoverage: +_data.totalActivePrimaryCoverage || 0,
    activeSpouseCoverage: +_data.totalActiveSpouseCoverage || 0,
    annuityCoverage: +_data.totalAnnuityCoverage || 0,
    annuityAmount: +_data.totalAnnuityCoverage || 0,
    childCoverage: +_data.totalChildCoverage || 0,
    primaryCoverage: +_data.totalPrimaryCoverage || 0,
    spouseCoverage: +_data.totalSpouseCoverage || 0,
    totalCoverage:
      (+_data.totalPrimaryCoverage || 0) +
      (+_data.totalSpouseCoverage || 0) +
      (+_data.totalChildCoverage || 0) +
      (+_data.totalAnnuityCoverage || 0),
    restricted: _data.access.toUpperCase() === 'NONE',
    canUpdateAll: _data.allowAllOtherUpdates === 'Y',
    canUpdateContactInfo: _data.allowContactInfoUpdate === 'Y',
    canPrintDocuments: _data.allowDocumentsPrint === 'Y',
    canMakeOneTimePayment: _data.allowOneTimePayment === 'Y',
    canUpdatePaymentInfo: _data.allowPaymentInfoUpdates === 'Y',

    // policyData
    status: policyData?.policyStatus ?? '',
    statusText: policyData?.policyStatusLong ?? '',
    active: policyData?.policyStatusLong === 'Active',
    pending: policyData?.policyStatusLong === 'Pending',
    terminated: policyData?.policyStatusLong === 'Terminated',
    billingForm: policyData?.billingForm ?? '',
    billingMode: policyData?.billingMode ?? '',
    modePremium: policyData?.modePremium ? parseNum(policyData.modePremium) : 0,
    nextDraftDate: policyData?.nextSchedDraftDate,
    issueDate: policyData ? parseDate(policyData.issueDate, 'MM/dd/yyyy') : null,
    billToDate: policyData ? parseDate(policyData.billToDate, 'MM/dd/yyyy') : null,
    recordId: policyData?.policyRecordId ?? '',
    recordStatus: policyData?.policyRecordStatus ?? '',
    restrictCode: policyData?.restrictCode ?? '',
    state: policyData?.state ?? '',
    policyNumber: policyData?.policyNum ?? '',
    primaryInsuredIsOwner: policyData?.primaryInsuredIsOwner === 'true',
    clientEmail: policyData?.clientEmailAddress ?? undefined,
    wipRecordExists: policyData?.wipRecordExists === 'true',
    bankName: policyData?.bankName ?? '',
    paidToDate: policyData ? parseDate(policyData.paidToDate, 'MM/dd/yyyy') : null,
    nextPaymentDueDate: policyData ? parseDate(policyData.nextPaymentDueDate, 'MM/dd/yyyy') : null,
    rvpName: policyData?.servicingRVPName ?? '',
    isRvpRvp: policyData?.servicingRVPTitle === 'RVP',
    rvpTitleDesc: policyData?.servicingRVPTitleDesc ?? '',
    isA1BillingForm: policyData?.billingForm === 'A1',
    isB2BillingForm: policyData?.billingForm === 'B2',
    writingAgent: policyData
      ? {
          id: policyData.writingAgent || '',
          name: policyData.writingAgentName || '',
          status: policyData.writingAgentStatus || '',
        }
      : undefined,
    writingAgent2: policyData
      ? {
          id: policyData.writingAgent2 ?? '',
          name: policyData.writingAgentName2 ?? '',
          status: policyData.writingAgentStatus2 ?? '',
        }
      : undefined,
  } as Partial<IPolicyStorePolicy>;
  const tn = mapPersonResponse({
    PersonFirstName: insuredFirstName,
    PersonLastName: insuredLastName,
  });
  const n: Partial<IPolicyStorePolicy> = {
    insuredName: `${tn.firstName} ${tn.lastName}`,
    primaryInsured: tn,
  };
  // policyData
  const p: Partial<IPolicyStorePolicy> = {
    payments: policyData ? _setPayments(policyData.payments) : undefined,
    beneficiaries: policyData ? _setBeneficiaries(policyData.beneficiaries) : undefined,
    phases: policyData ? _setPhases(policyData.phases) : undefined,
    annuity: policyData ? _setAnnuity(policyData.phases) : undefined,
    childRider: policyData ? _setChildRider(policyData.phases) : undefined,
    owner: policyData ? _setOwner(policyData) : undefined,
    // paymentInfo: _setPaymentInfo(policyData), // TODO: payment info is a separate call, see polictStore.setPaymentInfo
  };
  // reinstatement
  const r: Partial<IPolicyStorePolicy> = {};
  r.reinstatement = reinstatement ? _setReinstatement(reinstatement) : undefined;

  const isCanadaQuebecPolicy = p.owner?.address.state === 'PQ';
  const isCanadaQuebecBeneficiaryPolicy = p.phases
    ? _isCanadaQuebecBeneficiaryPolicy(p.phases as IPhase[], p.owner as IOwner)
    : false;
  const isCanadaPolicy = _isCanadaPolicy(contractId);
  const allowEditBeneficiary = _allowEditBeneficiary(
    isCanadaPolicy,
    p.phases as IPhase[],
    _data.thirdPartyPolicy
  );
  const dateFormat = _dateFormat(isCanadaQuebecPolicy, isCanadaPolicy);
  const isNewYorkPolicy = _isNewYorkPolicy(contractId);
  const isUSPolicy = _isUSPolicy(contractId);
  const country = _country(isCanadaPolicy);
  const d = {
    isCanadaQuebecPolicy,
    isCanadaQuebecBeneficiaryPolicy,
    isCanadaPolicy,
    allowEditBeneficiary,
    dateFormat,
    isNewYorkPolicy,
    isUSPolicy,
    country,
  };
  const mappedPolicy = { ...t, ...p, ...n, ...r, ...d, ...a } as IPolicyStorePolicy;
  return mappedPolicy;
};
